import { useState } from "react";
import { AlertTriangle, FileText, Gauge, Pencil, Plus, RefreshCw, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { StatusPill } from "@/components/common";
import { fmtDate } from "@/lib/format";
import { money } from "@/components/inventory/util";

const STATE_RGB = { OK: "var(--glow-money)", "Due soon": "var(--glow-lists)", Overdue: "239,68,68" };
const mi = (n) => `${Number(n).toLocaleString("en-US")} mi`;

export function dueText(s) {
  const parts = [];
  if (s.due_miles != null) parts.push(s.miles_left < 0 ? `${mi(-s.miles_left)} over` : `${mi(s.miles_left)} left`);
  if (s.due_date) parts.push(s.days_left < 0 ? `${-s.days_left} days late` : `by ${fmtDate(s.due_date)}`);
  return parts.join(" · ");
}

export function Schedules({ v, locked, onEdit }) {
  return (
    <ul className="grid grid-cols-1 gap-2 md:grid-cols-2" data-testid="vehicle-schedules">{v.schedules.map((s) => (
      <li key={s.key} className="q-card flex min-w-0 items-center gap-3 p-3" data-testid={`schedule-${s.key}`}>
        <span className="min-w-0 flex-1"><b className="block text-q-text">{s.name}</b><span className="block text-xs text-q-muted" data-testid={`schedule-due-${s.key}`}>{dueText(s)}</span></span>
        <StatusPill label={s.state} rgb={STATE_RGB[s.state]} testid={`schedule-state-${s.key}`} />
        <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" disabled={locked} onClick={() => onEdit(s)} aria-label={`Edit ${s.name}`} data-testid={`schedule-edit-${s.key}`}><Pencil className="h-4 w-4" /></button>
      </li>))}
    </ul>
  );
}

export function Recalls({ base, v, locked, onData }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const r = v.recalls;
  const check = () => { setBusy(true); setErr(""); api.post(`${base}/${v.id}/recalls`).then(({ data }) => { setErr(data.recall_error || ""); onData(data); }).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false)); };
  return (
    <section data-testid="vehicle-recalls">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg font-extrabold text-q-text">Recalls {r.checked_at && <span className="text-sm font-normal text-q-muted" data-testid="recall-checked">checked {fmtDate(r.checked_at.slice(0, 10))}</span>}</h3>
        <button className="q-btn q-btn-secondary min-h-[44px]" disabled={busy || locked} onClick={check} data-testid="recall-check-btn"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Check recalls</button>
      </div>
      {err && <p className="mb-2 flex items-center gap-2 rounded-xl bg-q-raised p-3 text-sm text-q-text" data-testid="recall-error"><AlertTriangle className="h-4 w-4 shrink-0" />{err}</p>}
      {r.message && <p className="text-sm text-q-muted" data-testid="recall-none">{r.message} · {fmtDate(r.checked_at.slice(0, 10))}</p>}
      {!r.checked_at && !err && <p className="text-sm text-q-muted">Not checked yet. We also re-check every week.</p>}
      <p className="sr-only" data-testid="recall-count">{r.count}</p>
      <ul className="space-y-2">{(r.campaigns || []).map((c) => (
        <li key={c.number} className="q-card p-3 text-sm" data-testid={`recall-${c.number}`}>
          <p className="flex flex-wrap items-center gap-2 font-semibold text-q-text"><ShieldAlert className="h-4 w-4" style={{ color: "rgb(239,68,68)" }} />{c.number} · {c.component}</p>
          <p className="mt-1 text-xs text-q-muted">{c.date && fmtDate(c.date)}</p>
          <p className="mt-1 text-q-text">{c.summary}</p>
          {c.remedy && <p className="mt-1 text-q-muted"><b>Remedy:</b> {c.remedy}</p>}
        </li>))}
      </ul>
    </section>
  );
}

export function Services({ v, locked, onAdd, onEdit, onDelete }) {
  return (
    <section data-testid="vehicle-services">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2"><h3 className="font-display text-lg font-extrabold text-q-text">Service history</h3>
        <button className="q-btn q-btn-primary min-h-[44px]" disabled={locked} onClick={onAdd} data-testid="service-add-btn"><Plus className="h-4 w-4" /> Log service</button></div>
      {!v.services.length && <p className="text-sm text-q-muted" data-testid="services-empty">No services logged yet.</p>}
      <ul className="space-y-2">{v.services.map((s) => (
        <li key={s.id} className="q-card flex min-w-0 flex-wrap items-center gap-3 p-3" data-testid={`service-${s.id}`}>
          <span className="min-w-0 flex-1"><b className="block truncate text-q-text">{s.service}</b><span className="block truncate text-xs text-q-muted">{fmtDate(s.date)} · {mi(s.mileage)}{s.shop ? ` · ${s.shop}` : ""}{s.receipt_doc_id ? " · Receipt in Documents" : ""}</span></span>
          {s.cost > 0 && <b className="font-display text-q-text" data-testid={`service-cost-${s.id}`}>{money(s.cost)}</b>}
          <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" disabled={locked} onClick={() => onEdit(s)} aria-label="Edit" data-testid={`service-edit-${s.id}`}><Pencil className="h-4 w-4" /></button>
          <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" disabled={locked} onClick={() => onDelete(s)} aria-label="Delete" data-testid={`service-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
        </li>))}
      </ul>
    </section>
  );
}

export function Docs({ v }) {
  return (
    <section data-testid="vehicle-docs">
      <h3 className="mb-2 font-display text-lg font-extrabold text-q-text">Documents</h3>
      {!v.documents.length && <p className="text-sm text-q-muted" data-testid="vehicle-docs-empty">No documents linked. Receipts from services land here.</p>}
      <ul className="space-y-2">{v.documents.map((d) => (
        <li key={d.id} className="flex min-h-[44px] items-center gap-2 rounded-xl bg-q-raised p-2 text-sm" data-testid={`vehicle-doc-${d.id}`}>
          <FileText className="h-4 w-4 shrink-0 text-q-muted" /><span className="min-w-0 flex-1 truncate text-q-text">{d.sensitive ? "Locked document (open it in Documents)" : d.title}</span><span className="text-xs text-q-muted">{d.category}</span>
        </li>))}
      </ul>
    </section>
  );
}

export function Profile({ v, onOdo, locked }) {
  const rows = [["Make", v.make], ["Model", v.model], ["Year", v.year], ["Trim", v.trim], ["VIN", v.vin_masked], ["Plate", v.plate], ["Color", v.color], ["Tire size", v.tire_size],
    ["Registration renews", v.registration_date && fmtDate(v.registration_date)], ["Inspection due", v.inspection_date && fmtDate(v.inspection_date)], ["Insurance renews", v.insurance_date && fmtDate(v.insurance_date)]];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-display text-3xl font-extrabold text-q-text" data-testid="vehicle-odometer">{mi(v.odometer)}</p>
        <button className="q-btn q-btn-secondary min-h-[44px]" disabled={locked} onClick={onOdo} data-testid="odometer-btn"><Gauge className="h-4 w-4" /> Update odometer</button>
      </div>
      <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">{rows.filter(([, x]) => x).map(([k, x]) => (
        <div key={k} className="min-w-0"><dt className="text-xs text-q-muted">{k}</dt><dd className="break-words text-q-text" data-testid={`vehicle-field-${k.toLowerCase().replace(/\s+/g, "-")}`}>{x}</dd></div>))}
      </dl>
    </div>
  );
}
